// src/hooks/useList/utils.ts
import { startOfDay, endOfDay } from "date-fns";
import qs from "qs";
import { FilterMap, ListParams, SortOption } from "./types";

/**
 * Transforms filters for API request
 */
export const transformFilters = (filters: FilterMap): Record<string, any> => {
  const transformedFilters: Record<string, any> = {};

  Object.keys(filters)
    .filter((key) => !key.startsWith("__"))
    .forEach((key) => {
      const value = filters[key];

      // Handle date filters
      if (
        value instanceof Date &&
        !key.includes("$gte") &&
        !key.includes("$lte")
      ) {
        const startDay = startOfDay(value);
        const endDay = endOfDay(value);

        transformedFilters[key] = {
          $gte: startDay,
          $lte: endDay,
        };
      }
      // Handle date range filters
      else if (
        value instanceof Date &&
        (key.includes("$gte") || key.includes("$lte"))
      ) {
        const date = startOfDay(value);

        if (key.includes("$gte")) {
          date.setUTCHours(0, 0, 0, 0);
        } else {
          date.setUTCHours(23, 59, 59, 999);
        }

        transformedFilters[key] = date;
      }
      // Handle operator filters (field$eq, field$ne, etc.)
      else if (typeof value === "string" && key.includes("$")) {
        const [fieldName, operator = "eq"] = key.split("$");
        transformedFilters[fieldName] = { [`$${operator}`]: value };
      }
      // Handle regular filters
      else {
        transformedFilters[key] = value;
      }
    });

  return transformedFilters;
};

/**
 * Creates query string from list parameters
 */
export const createQueryString = (
  params: ListParams,
  additionalParams: Record<string, any> = {},
  companySidebarFilter?: any,
  ignoreSideBarFilter?: boolean,
  endpointPath?: string
): string => {
  const { pagination, filters, sort, populate, fields } = params;

  // For consolidated endpoints, use Prisma-style parameters
  if (endpointPath?.includes("consolidated")) {
    const query: Record<string, any> = {
      ...additionalParams,
      page: pagination.pageIndex + 1,
      rowsPerPage: pagination.pageSize,
    };

    // Handle orderReference filter (for receptions: filter by linked order reference)
    if ((filters as any).orderReference) {
      const v: any = (filters as any).orderReference;
      if (typeof v === "string") {
        query.orderReference = v;
      } else if (typeof v === "object" && v !== null) {
        // support legacy operator shapes if they leak into consolidated mode
        if ("$contains" in v || "$containsi" in v) {
          query.orderReference = (v as any).$contains || (v as any).$containsi;
        } else if ("$eq" in v) {
          query.orderReference = (v as any).$eq;
        }
      }
    }

    // Transform filters to Prisma format
    // Handle providerType filter
    if (filters.providerType) {
      if (Array.isArray(filters.providerType)) {
        query.providerType = filters.providerType.join(",");
      } else if (
        typeof filters.providerType === "object" &&
        filters.providerType !== null
      ) {
        if (
          "$in" in filters.providerType &&
          Array.isArray(filters.providerType.$in)
        ) {
          query.providerType = filters.providerType.$in.join(",");
        } else if ("$eq" in filters.providerType) {
          query.providerType = filters.providerType.$eq;
        }
      } else if (typeof filters.providerType === "string") {
        query.providerType = filters.providerType;
      }
    }

    // Handle status filter
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query.status = filters.status.join(",");
      } else if (
        typeof filters.status === "object" &&
        filters.status !== null
      ) {
        if ("$in" in filters.status && Array.isArray(filters.status.$in)) {
          query.status = filters.status.$in.join(",");
        } else if ("$eq" in filters.status) {
          query.status = filters.status.$eq;
        }
      } else if (typeof filters.status === "string") {
        query.status = filters.status;
      }
    }

    // Handle company filter - support both object format ($eq, $in) and array format
    if (filters.company) {
      if (Array.isArray(filters.company)) {
        query.company = filters.company.join(",");
      } else if (
        typeof filters.company === "object" &&
        filters.company !== null
      ) {
        if ("$in" in filters.company && Array.isArray(filters.company.$in)) {
          query.company = filters.company.$in.join(",");
        } else if ("$eq" in filters.company) {
          query.company = filters.company.$eq;
        }
      } else if (typeof filters.company === "string") {
        query.company = filters.company;
      }
    }

    // Handle $or filter - extract company from $or conditions (for client/contributor logic)
    if (filters.$or && Array.isArray(filters.$or)) {
      // Look for company filter in $or conditions
      const companyFromOr = filters.$or.find(
        (orCondition: any) =>
          orCondition?.company?.$eq ||
          (orCondition?.company?.$in && Array.isArray(orCondition.company.$in))
      );
      if (companyFromOr?.company?.$eq) {
        query.company = companyFromOr.company.$eq;
      } else if (
        companyFromOr?.company?.$in &&
        Array.isArray(companyFromOr.company.$in)
      ) {
        query.company = companyFromOr.company.$in.join(",");
      }
    }

    // Handle type filter (Type appro: Stock, Chantier, Projet, Multiple) → suborderType for order API
    if (filters.type !== undefined && filters.type !== null) {
      if (Array.isArray(filters.type)) {
        query.suborderType = "multiple";
      } else if (typeof filters.type === "string") {
        query.suborderType = filters.type;
      } else if (
        typeof filters.type === "object" &&
        "$eq" in (filters.type as object)
      ) {
        query.suborderType = (filters.type as { $eq: string }).$eq;
      }
    }

    // Explicit suborderType (e.g. ["dockyard", "project"]) → suborderType for order API
    if (filters.suborderType !== undefined && filters.suborderType !== null) {
      query.suborderType = Array.isArray(filters.suborderType)
        ? filters.suborderType.join(",")
        : String(filters.suborderType);
    }

    // Handle warehouse filter
    if (filters.warehouse) {
      if (Array.isArray(filters.warehouse)) {
        query.warehouse = filters.warehouse.join(",");
      } else if (
        typeof filters.warehouse === "object" &&
        filters.warehouse !== null
      ) {
        if (
          "$in" in filters.warehouse &&
          Array.isArray(filters.warehouse.$in)
        ) {
          query.warehouse = filters.warehouse.$in.join(",");
        } else if ("$eq" in filters.warehouse) {
          query.warehouse = filters.warehouse.$eq;
        }
      } else if (typeof filters.warehouse === "string") {
        query.warehouse = filters.warehouse;
      }
    }

    // Handle provider filter
    if (filters.provider) {
      if (Array.isArray(filters.provider)) {
        query.provider = filters.provider.join(",");
      } else if (
        typeof filters.provider === "object" &&
        filters.provider !== null
      ) {
        if ("$in" in filters.provider && Array.isArray(filters.provider.$in)) {
          query.provider = filters.provider.$in.join(",");
        } else if ("$eq" in filters.provider) {
          query.provider = filters.provider.$eq;
        }
      } else if (typeof filters.provider === "string") {
        query.provider = filters.provider;
      }
    }

    // Handle global filter (Prisma: dockyard_<id>, project_<id>, reference_<id>, product_<id>)
    if (filters.global) {
      if (
        typeof filters.global === "object" &&
        filters.global !== null &&
        "$eq" in filters.global
      ) {
        query.global = filters.global.$eq;
      } else if (typeof filters.global === "string") {
        query.global = filters.global;
      }
    }

    // Handle dockyard filter (Prisma: dockyard=<id> for orders by suborder dockyard)
    if (filters.dockyard) {
      if (
        typeof filters.dockyard === "object" &&
        filters.dockyard !== null &&
        "$eq" in filters.dockyard
      ) {
        query.dockyard = filters.dockyard.$eq;
      } else if (typeof filters.dockyard === "string") {
        query.dockyard = filters.dockyard;
      } else if (Array.isArray(filters.dockyard)) {
        query.dockyard = filters.dockyard.join(",");
      }
    }

    let deliveryDateGte: any = null;
    let deliveryDateLte: any = null;

    if (filters["deliveryDate][$gte"]) {
      deliveryDateGte = filters["deliveryDate][$gte"];
    }
    if (filters["deliveryDate][$lte"]) {
      deliveryDateLte = filters["deliveryDate][$lte"];
    }

    const deliveryDateFilter = filters.deliveryDate;
    if (
      deliveryDateFilter &&
      typeof deliveryDateFilter === "object" &&
      !Array.isArray(deliveryDateFilter) &&
      !(deliveryDateFilter instanceof Date)
    ) {
      const dateFilterObj = deliveryDateFilter as Record<string, any>;
      if (dateFilterObj.$gte) {
        deliveryDateGte =
          dateFilterObj.$gte instanceof Date
            ? dateFilterObj.$gte.toISOString().split("T")[0]
            : dateFilterObj.$gte;
      }
      if (dateFilterObj.$lte) {
        deliveryDateLte =
          dateFilterObj.$lte instanceof Date
            ? dateFilterObj.$lte.toISOString().split("T")[0]
            : dateFilterObj.$lte;
      }
    }

    if (deliveryDateGte || deliveryDateLte) {
      if (deliveryDateGte) {
        query.deliveryDateGte =
          deliveryDateGte instanceof Date
            ? deliveryDateGte.toISOString().split("T")[0]
            : String(deliveryDateGte);
      }
      if (deliveryDateLte) {
        query.deliveryDateLte =
          deliveryDateLte instanceof Date
            ? deliveryDateLte.toISOString().split("T")[0]
            : String(deliveryDateLte);
      }
    } else if (deliveryDateFilter && typeof deliveryDateFilter === "string") {
      // Single date (exact match)
      query.deliveryDate = deliveryDateFilter;
    }

    // Handle reference filter (for receptions)
    if (filters.reference) {
      if (typeof filters.reference === "object" && filters.reference !== null) {
        const refFilter = filters.reference as Record<string, any>;
        if ("$contains" in refFilter || "$containsi" in refFilter) {
          query.reference = refFilter.$contains || refFilter.$containsi;
        } else if ("$eq" in refFilter) {
          query.reference = refFilter.$eq;
        }
      } else if (typeof filters.reference === "string") {
        query.reference = filters.reference;
      }
    }

    // Handle warehouseReference filter (for receptions)
    if (filters.warehouseReference) {
      if (
        typeof filters.warehouseReference === "object" &&
        filters.warehouseReference !== null
      ) {
        const whRefFilter = filters.warehouseReference as Record<string, any>;
        if ("$contains" in whRefFilter || "$containsi" in whRefFilter) {
          query.warehouseReference =
            whRefFilter.$contains || whRefFilter.$containsi;
        } else if ("$eq" in whRefFilter) {
          query.warehouseReference = whRefFilter.$eq;
        }
      } else if (typeof filters.warehouseReference === "string") {
        query.warehouseReference = filters.warehouseReference;
      }
    }

    // Handle blNumber filter (for receptions)
    if (filters.blNumber) {
      if (typeof filters.blNumber === "object" && filters.blNumber !== null) {
        const blFilter = filters.blNumber as Record<string, any>;
        if ("$contains" in blFilter || "$containsi" in blFilter) {
          query.blNumber = blFilter.$contains || blFilter.$containsi;
        } else if ("$eq" in blFilter) {
          query.blNumber = blFilter.$eq;
        }
      } else if (typeof filters.blNumber === "string") {
        query.blNumber = filters.blNumber;
      }
    }

    // Handle warehouseOrigin filter (for receptions)
    if (filters.warehouseOrigin) {
      if (Array.isArray(filters.warehouseOrigin)) {
        query.warehouseOrigin = filters.warehouseOrigin.join(",");
      } else if (
        typeof filters.warehouseOrigin === "object" &&
        filters.warehouseOrigin !== null
      ) {
        if (
          "$in" in filters.warehouseOrigin &&
          Array.isArray(filters.warehouseOrigin.$in)
        ) {
          query.warehouseOrigin = filters.warehouseOrigin.$in.join(",");
        } else if ("$eq" in filters.warehouseOrigin) {
          query.warehouseOrigin = filters.warehouseOrigin.$eq;
        }
      } else if (typeof filters.warehouseOrigin === "string") {
        query.warehouseOrigin = filters.warehouseOrigin;
      }
    }

    // Handle isTransfer filter (for receptions)
    if (filters.isTransfer !== undefined) {
      query.isTransfer =
        filters.isTransfer === true ||
        filters.isTransfer === "true" ||
        filters.isTransfer === 1;
    }

    // Handle receptionDate filter (date range for receptions)
    let receptionDateGte: any = null;
    let receptionDateLte: any = null;

    if (filters["receptionDate][$gte"]) {
      receptionDateGte = filters["receptionDate][$gte"];
    }
    if (filters["receptionDate][$lte"]) {
      receptionDateLte = filters["receptionDate][$lte"];
    }

    const receptionDateFilter = filters.receptionDate;
    if (
      receptionDateFilter &&
      typeof receptionDateFilter === "object" &&
      !Array.isArray(receptionDateFilter) &&
      !(receptionDateFilter instanceof Date)
    ) {
      const dateFilterObj = receptionDateFilter as Record<string, any>;
      if (dateFilterObj.$gte) {
        receptionDateGte =
          dateFilterObj.$gte instanceof Date
            ? dateFilterObj.$gte.toISOString().split("T")[0]
            : dateFilterObj.$gte;
      }
      if (dateFilterObj.$lte) {
        receptionDateLte =
          dateFilterObj.$lte instanceof Date
            ? dateFilterObj.$lte.toISOString().split("T")[0]
            : dateFilterObj.$lte;
      }
    }

    if (receptionDateGte || receptionDateLte) {
      if (receptionDateGte) {
        query.receptionDateGte =
          receptionDateGte instanceof Date
            ? receptionDateGte.toISOString().split("T")[0]
            : String(receptionDateGte);
      }
      if (receptionDateLte) {
        query.receptionDateLte =
          receptionDateLte instanceof Date
            ? receptionDateLte.toISOString().split("T")[0]
            : String(receptionDateLte);
      }
    } else if (receptionDateFilter && typeof receptionDateFilter === "string") {
      // Single date (exact match)
      query.receptionDate = receptionDateFilter;
    }

    // Handle referencingStatus filter (for receptions)
    if (filters.referencingStatus) {
      if (
        typeof filters.referencingStatus === "object" &&
        filters.referencingStatus !== null
      ) {
        if ("$eq" in filters.referencingStatus) {
          query.referencingStatus = filters.referencingStatus.$eq;
        }
      } else if (typeof filters.referencingStatus === "string") {
        query.referencingStatus = filters.referencingStatus;
      }
    }

    if (filters.orderId) {
      query.orderId = filters.orderId;
    }

    // Handle orderDate filter (date range for EDB IntegratedOrdersPopin)
    let orderDateGte: any = null;
    let orderDateLte: any = null;

    if (filters["orderDate][$gte"]) {
      orderDateGte = filters["orderDate][$gte"];
    }
    if (filters["orderDate][$lte"]) {
      orderDateLte = filters["orderDate][$lte"];
    }

    if (orderDateGte || orderDateLte) {
      if (orderDateGte) {
        query.orderDateGte =
          orderDateGte instanceof Date
            ? orderDateGte.toISOString().split("T")[0]
            : String(orderDateGte);
      }
      if (orderDateLte) {
        query.orderDateLte =
          orderDateLte instanceof Date
            ? orderDateLte.toISOString().split("T")[0]
            : String(orderDateLte);
      }
    }

    // Handle nested suborder filters for EDB IntegratedOrdersPopin
    // suborders][project
    const suborderProject = (filters as any)["suborders][project"];
    if (suborderProject) {
      if (Array.isArray(suborderProject)) {
        query.suborderProject = suborderProject.join(",");
      } else if (typeof suborderProject === "string") {
        query.suborderProject = suborderProject;
      }
    }

    // suborders][dockyard
    const suborderDockyard = (filters as any)["suborders][dockyard"];
    if (suborderDockyard) {
      if (Array.isArray(suborderDockyard)) {
        query.suborderDockyard = suborderDockyard.join(",");
      } else if (typeof suborderDockyard === "string") {
        query.suborderDockyard = suborderDockyard;
      }
    }

    // suborders][type][$in
    const suborderType = (filters as any)["suborders][type][$in"];
    if (suborderType) {
      if (Array.isArray(suborderType)) {
        query.suborderType = suborderType.join(",");
      } else if (typeof suborderType === "string") {
        query.suborderType = suborderType;
      }
    }

    // suborders][type][$ne or $notIn (Strapi): exclude stock => only dockyard/project
    if (!query.suborderType) {
      const suborderTypeNe =
        (filters as any)["suborders][type][$ne"] ??
        (filters as any)["suborders][type][$notIn"];
      if (suborderTypeNe) {
        const neValues = Array.isArray(suborderTypeNe)
          ? suborderTypeNe
          : [suborderTypeNe];
        const neValuesLower = neValues
          .map((v) => String(v).toLowerCase())
          .filter(Boolean);
        if (neValuesLower.includes("stock")) {
          query.suborderType = "dockyard,project";
        }
      }
    }

    // suborders][suborderProducts][reference
    const suborderProductReference = (filters as any)[
      "suborders][suborderProducts][reference"
    ];
    if (suborderProductReference) {
      if (Array.isArray(suborderProductReference)) {
        query.suborderProductReference = suborderProductReference.join(",");
      } else if (typeof suborderProductReference === "string") {
        query.suborderProductReference = suborderProductReference;
      }
    }

    // receptions][receptionReferences][orders][reference
    const receptionReferenceOrderReference = (filters as any)[
      "receptions][receptionReferences][orders][reference"
    ];
    if (receptionReferenceOrderReference) {
      if (Array.isArray(receptionReferenceOrderReference)) {
        query.receptionReferenceOrderReference =
          receptionReferenceOrderReference.join(",");
      } else if (typeof receptionReferenceOrderReference === "string") {
        query.receptionReferenceOrderReference =
          receptionReferenceOrderReference;
      }
    }

    // receptionId (Order API / Prisma): filter orders linked to selected receptions
    const { receptionId } = filters as any;
    if (receptionId) {
      if (Array.isArray(receptionId)) {
        query.receptionId = receptionId.join(",");
      } else if (
        typeof receptionId === "string" ||
        typeof receptionId === "number"
      ) {
        query.receptionId = String(receptionId);
      }
    }

    // Handle reference filter (array of order references)
    if (filters.reference) {
      if (Array.isArray(filters.reference)) {
        query.reference = filters.reference.join(",");
      } else if (typeof filters.reference === "string") {
        query.reference = filters.reference;
      }
    }

    // Add company filter from sidebar if not ignored (this will override any existing company filter)
    if (companySidebarFilter && !ignoreSideBarFilter) {
      query.company = companySidebarFilter._id;
    }

    // Add sort
    if (sort) {
      const sortValue = `${sort.field}:${sort.direction}`;
      query.sort = sortValue;
    }

    return qs.stringify(query);
  }

  // Default Strapi-style parameters for other endpoints
  const query: Record<string, any> = {
    ...additionalParams,
    pagination: {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    },
    filters: transformFilters(filters),
  };

  // Skip populate parameter for consolidated endpoints as Prisma API includes all relations automatically
  if (populate && !endpointPath?.includes("consolidated")) {
    query.populate = populate;
  }

  if (fields) {
    query.fields = fields;
  }

  if (sort) {
    query.sort = [`${sort.field}:${sort.direction}`];
  }

  // Add company filter from sidebar if available and not ignored
  if (companySidebarFilter && endpointPath !== "edbs" && !ignoreSideBarFilter) {
    query.filters.company = { $eq: companySidebarFilter._id };
  }

  return qs.stringify(query, { encodeValuesOnly: true });
};

/**
 * Parse string sort parameter into SortOption
 */
export const parseSortString = (
  sortString?: string
): SortOption | undefined => {
  if (!sortString) return undefined;

  const [field, direction = "asc"] = sortString.split(":");
  return {
    field,
    direction: direction as "asc" | "desc",
  };
};

/**
 * Get full URL with query string
 */
export const getFullUrl = (
  baseUrl: string,
  params: ListParams,
  additionalParams: Record<string, any> = {},
  companySidebarFilter?: any,
  ignoreSideBarFilter?: boolean
): string => {
  const queryString = createQueryString(
    params,
    additionalParams,
    companySidebarFilter,
    ignoreSideBarFilter,
    baseUrl
  );

  return `${baseUrl}${queryString ? `?${queryString}` : ""}`;
};
